// pages/AdminPage.jsx
import React, { useState, useEffect, Suspense, lazy } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2,
  LogOut,
  Key,
  Users,
  Hash,
  CheckCircle,
  Calendar,
} from "lucide-react";
import { Link } from "react-router-dom";

// Import components
import LoginForm from "@/components/auth/LoginForm";
import { StatsCard } from "@/components/ui/StatsCard";
import { usePasswordManager } from "@/hooks/usePasswordManager";

// Lazy load heavy components for better initial load performance
const CommunityPasswordTab = lazy(() => import("@/components/admin/CommunityPasswordTab"));
const WhitelistEntriesTab = lazy(() => import("@/components/admin/WhitelistEntriesTab"));

// GAIB Header Component
const GAIBHeader = () => (
  <div className="flex items-center space-x-4 mb-6">
    <Link to="/" className="flex items-center gap-2">
      <img
        src="/lovable-uploads/61a722fd-1842-4dd1-9fe5-80ed4f5ce6e7.png"
        alt="GAIB Logo"
        className="h-6 md:h-8 w-auto"
      />
    </Link>
  </div>
);

// GAIB Styled Stats Card with Glassmorphism
const GAIBStatsCard = ({ title, value, icon: Icon }) => (
  <div className="bg-white/20 backdrop-blur-lg p-6 rounded-2xl border border-white/30 shadow-lg hover:bg-white/25 transition-all duration-300 transform hover:scale-[1.02]">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm text-black/70 font-medium">{title}</div>
        <div className="text-3xl font-bold text-[#19E3E3] mt-1">{value}</div>
      </div>
      <div className="bg-white/20 p-3 rounded-xl">
        <Icon className="h-6 w-6 text-black/60" />
      </div>
    </div>
  </div>
);

const AdminDashboard = React.memo(({ user, onLogout }) => {
  const [loggingOut, setLoggingOut] = useState(false);
  const [activeTab, setActiveTab] = useState("passwords");
  const [whitelistEntries, setWhitelistEntries] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Use the password manager hook to get password data (no duplication)
  const { passwords, loading: passwordsLoading, totalCount, activeCount, totalUsages } = usePasswordManager();

  useEffect(() => {
    fetchWhitelistData();
  }, []);

  const fetchWhitelistData = async () => {
    try {
      setStatsLoading(true);
      // Only fetch whitelist entries - password data is handled by usePasswordManager
      const { data: whitelistData, error: whitelistError } = await supabase
        .from("whitelist_registrations")
        .select("*")
        .order("created_at", { ascending: false });

      if (whitelistError) throw whitelistError;

      setWhitelistEntries(whitelistData || []);
    } catch (err) {
      console.error("Error fetching whitelist data:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await onLogout();
    setLoggingOut(false);
  };

  // Calculate stats (show loading or actual data)
  const isLoading = passwordsLoading || statsLoading;
  
  // Use the actual counts from the database instead of the limited array
  const totalPasswordsCount = isLoading ? "..." : totalCount;
  const activePasswordsCount = isLoading ? "..." : activeCount;
  const totalRegistrations = statsLoading ? "..." : whitelistEntries.length;
  const totalUsagesCount = isLoading ? "..." : totalUsages;

  const tabs = [
    {
      id: "passwords",
      name: "Community Passwords",
      icon: Key,
    },
    {
      id: "whitelist",
      name: "Whitelist Entries",
      icon: Users,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-200 via-orange-300 to-orange-400 relative overflow-hidden">
      {/* Layout Background Elements */}
      <img
        src="/lovable-uploads/1a8cc40d-5db4-4af8-892a-0d9d139ffb8c.png"
        alt=""
        className="clouds-top-right"
        aria-hidden="true"
      />
      <img
        src="/lovable-uploads/96315555-c76f-42b9-a5f7-403e312f654e.png"
        alt=""
        className="circles-top-left"
        aria-hidden="true"
      />
      <img
        src="/lovable-uploads/cce19565-8e57-4e6e-87b3-8a5ba9ee91c9.png"
        alt=""
        className="noise-overlay"
        aria-hidden="true"
      />
      <img
        src="/lovable-uploads/8a797feb-705c-4580-a886-fff6a268cf33.png"
        alt=""
        className="desert-bottom"
        aria-hidden="true"
      />

      <div className="relative z-10 container mx-auto py-8 px-4">
        {/* Header with Glassmorphism */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/30 p-6 mb-8 shadow-xl">
          <div className="flex justify-between items-start">
            <div>
              <GAIBHeader />
              <h1 className="text-4xl font-bold text-black mb-2 drop-shadow-sm">
                Admin Dashboard
              </h1>
              <p className="text-black/70">Welcome back, {user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="flex items-center px-6 py-3 bg-red-500/80 hover:bg-red-500 disabled:opacity-50 text-white rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/20 transform hover:scale-[1.02]"
            >
              {loggingOut ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <LogOut className="h-4 w-4 mr-2" />
              )}
              Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <GAIBStatsCard
            title="Total Passwords"
            value={totalPasswordsCount}
            icon={Hash}
          />
          <GAIBStatsCard
            title="Active Passwords"
            value={activePasswordsCount}
            icon={CheckCircle}
          />
          <GAIBStatsCard
            title="Total Registrations"
            value={totalRegistrations}
            icon={Users}
          />
          <GAIBStatsCard
            title="Total Usages"
            value={totalUsagesCount}
            icon={Calendar}
          />
        </div>

        {/* Tab Navigation with Glassmorphism */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/30 p-6 mb-8 shadow-xl">
          <div className="border-b border-white/20 pb-4">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-3 px-4 rounded-xl font-medium text-sm transition-all duration-300 ${
                      activeTab === tab.id
                        ? "bg-[#19E3E3]/20 text-black border border-[#19E3E3]/30 backdrop-blur-sm"
                        : "text-black/70 hover:text-black hover:bg-white/10 border border-transparent"
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content with Glassmorphism Container */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/30 p-8 shadow-xl min-h-[400px]">
          <Suspense 
            fallback={
              <div className="flex items-center justify-center py-12">
                <div className="bg-white/20 backdrop-blur-xl p-6 rounded-2xl border border-white/30 shadow-lg">
                  <Loader2 className="h-8 w-8 animate-spin text-[#19E3E3] mx-auto mb-3" />
                  <p className="text-sm text-black/70 text-center">Loading {activeTab === "passwords" ? "Password Manager" : "Whitelist Entries"}...</p>
                </div>
              </div>
            }
          >
            {activeTab === "passwords" && <CommunityPasswordTab />}
            {activeTab === "whitelist" && <WhitelistEntriesTab />}
          </Suspense>
        </div>
      </div>
    </div>
  );
});

// Main Admin Page Component
const AdminPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
      setInitialLoading(false);
    };

    checkUser();
  }, []);

  const handleLogin = async (email, password) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setLoading(false);
      throw error;
    }

    setUser(data.user);
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-200 via-orange-300 to-orange-400 relative overflow-hidden">
        {/* Layout Background Elements */}
        <img
          src="/lovable-uploads/1a8cc40d-5db4-4af8-892a-0d9d139ffb8c.png"
          alt=""
          className="clouds-top-right"
          aria-hidden="true"
        />
        <img
          src="/lovable-uploads/96315555-c76f-42b9-a5f7-403e312f654e.png"
          alt=""
          className="circles-top-left"
          aria-hidden="true"
        />
        <img
          src="/lovable-uploads/cce19565-8e57-4e6e-87b3-8a5ba9ee91c9.png"
          alt=""
          className="noise-overlay"
          aria-hidden="true"
        />
        <img
          src="/lovable-uploads/8a797feb-705c-4580-a886-fff6a268cf33.png"
          alt=""
          className="desert-bottom"
          aria-hidden="true"
        />

        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="bg-white/20 backdrop-blur-xl p-8 rounded-3xl border border-white/30 shadow-xl">
            <Loader2 className="h-8 w-8 animate-spin text-[#19E3E3] mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return user ? (
    <AdminDashboard user={user} onLogout={handleLogout} />
  ) : (
    <LoginForm onLogin={handleLogin} loading={loading} />
  );
};

export default AdminPage;
